<template>
  <div class="col-12 _col-xl-8 d-flex">
    <div class="card flex-grow-1">
      <h6 class="card-header">Transferts</h6>
      <div class="card-body position-relative">
        <div class="d-flex align-items-stretch">
          <div class="d-flex flex-column small fw-semibold text-nowrap me-2">
            <div v-for="direction in transferGroups" :key="direction.direction" class="d-flex align-items-center" style="height: 60px;">
              {{ direction.label }}
            </div>
          </div>
          <BarHorizontalComponent
            class="flex-grow-1"
            :bar-styles="'height: '+(Math.max(30, transferGroups.length*60))+'px;'"
            :data="datas"
            hide-zero-tooltip-items
            @click="itemOnClick"
          />
        </div>
        <div class="small text-muted d-flex gap-1 mt-2">
          <div v-for="direction in transferGroups" :key="`${direction.direction}-legend`" class="d-flex gap-3 flex-wrap me-3">
            <!-- <b>{{ direction.label }}: </b> -->
            <div v-for="transfer in direction.transfers" :key="transfer.id">{{ transfer.label }}: <b>{{ transfer.count }}</b></div>
          </div>
          <div class="ms-auto">Total Inscrits: <b>{{ totalInscrits }}</b><small v-if="companions.length > 0"> (dont {{ companionOnlyCount }} accompagnants)</small></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import * as ChartHelper from '@/compositions/chartHelper';
  import { computed } from 'vue';
  import { useBoardFilters } from '@/compositions/useBoardFilters';
  import { GetTransferDirectionLabel } from '@/compositions/helper';

  const { travel, participations, companions, filters } = useBoardFilters({ applyStatus: false })

  const inscrits = computed(() => {
    return participations.value.filter(x => x.invitation?.completionDate != null && x.invitation?.refusalDate == null) ?? [];
  });

  const companionOnlyCount = computed(() => companions.value.length ?? 0);

  const totalInscrits = computed(() => inscrits.value.length + companions.value.length);

  const transferDirections = [
    { direction: TravelDbTransferDirectionEnum.Outbound, label: GetTransferDirectionLabel(TravelDbTransferDirectionEnum.Outbound) },
    { direction: TravelDbTransferDirectionEnum.Return, label: GetTransferDirectionLabel(TravelDbTransferDirectionEnum.Return) },
    { direction: TravelDbTransferDirectionEnum.RoundTrip, label: GetTransferDirectionLabel(TravelDbTransferDirectionEnum.RoundTrip) },
  ];

  const getParticipationTransferId = (
    participation: (typeof inscrits.value)[number],
    direction: TravelDbTransferDirectionEnum,
  ) => {
    switch (direction) {
      case TravelDbTransferDirectionEnum.Outbound:
        return participation.transfer?.outboundTravelDbTransferId;
      case TravelDbTransferDirectionEnum.Return:
        return participation.transfer?.returnTravelDbTransferId;
      case TravelDbTransferDirectionEnum.RoundTrip:
        return participation.transfer?.roundTripTravelDbTransferId;
    }
  };

  const countTransfer = (direction: TravelDbTransferDirectionEnum, transferId: number) => {
    return inscrits.value.reduce((count, participation) => {
      if (getParticipationTransferId(participation, direction) !== transferId) return count;

      const companionCount = filters?.value.hideCompanions ? 0 : participation.companions?.length ?? 0;
      return count + 1 + companionCount;
    }, 0);
  };

  const transferGroups = computed(() => transferDirections.map(({ direction, label }) => ({
    direction,
    label,
    transfers: (travel?.value?.transfers ?? [])
      .filter(transfer => transfer.direction === direction)
      .map(transfer => ({
        id: transfer.id,
        label: transfer.name,
        count: countTransfer(direction, transfer.id),
      }))
      .filter(transfer => transfer.count > 0),
  })).filter(group => group.transfers.length > 0)
);

  const allTransfers = computed(() => transferGroups.value.flatMap(group => group.transfers.map(transfer => ({
    ...transfer,
    direction: group.direction,
    directionLabel: group.label,
  }))));

  const datas = computed(() => ({
    labels: transferGroups.value.map(group => group.label),
    datasets: allTransfers.value.map((transfer, index) => {
      const colorIndex = index % ChartHelper.Colors.length;

      return {
        customData: transfer.id,
        label: `${transfer.directionLabel} - ${transfer.label}`,
        data: transferGroups.value.map(group => group.direction === transfer.direction ? transfer.count : 0),
        backgroundColor: transferGroups.value.map(() => ChartHelper.Colors[colorIndex]),
        borderColor: transferGroups.value.map(() => ChartHelper.Borders[colorIndex % ChartHelper.Borders.length]),
      };
    }),
  }));

  const itemOnClick = (label: string | undefined, value: number | [number, number] | undefined | null, index: number) => {
    if(filters && datas.value.datasets[index]?.customData) {
      const transferId = datas.value.datasets[index].customData;
      filters.value.transfertId = { id: transferId, label:label??'' };
    }
  };
</script>
